import api, {
    LIST,
    STOREFRONT_BANNERS,
    STOREFRONT_SECTIONS,
    STOREFRONT_SETTINGS,
    STOREFRONT_STYLES,
    STOREFRONT_TEMPLATES_TYPE
} from '../Api'
import {ICollectionResponse} from "../../models/ICollectionResponse";
import {
    ICreateStorefrontFromTemplateRequest,
    ISaveStorefrontTemplateRequest,
    IStorefrontTemplate
} from "../../models/Storefront";
import {
    IApplyStorefrontTemplateRequest,
    IStorefrontTemplateApplyPlan
} from "../../models/StorefrontTemplateApply";
import {IActionResponse, IUploadFileResponse} from "../../dal/BaseDAL";
import {ArrayBufferToBase64} from "../../logic/ArrayBufferConverter";

const storefrontTemplatesApi = api.injectEndpoints({
    endpoints: (build) => ({
        getStorefrontTemplates: build.query<ICollectionResponse<IStorefrontTemplate>, void>({
            query: () => 'storefront/storefronts/templates',
            providesTags: (result) => result?.items
                ? [
                    ...result.items.map(t => ({type: STOREFRONT_TEMPLATES_TYPE, id: t.id} as const)),
                    {type: STOREFRONT_TEMPLATES_TYPE, id: LIST}
                ]
                : [{type: STOREFRONT_TEMPLATES_TYPE, id: LIST}]
        }),
        getStorefrontTemplate: build.query<IStorefrontTemplate, string>({
            query: (id) => `storefront/storefronts/templates/${id}`,
            providesTags: (_result, _error, id) => [{type: STOREFRONT_TEMPLATES_TYPE, id}]
        }),
        // Create a template from an existing storefront.
        saveStorefrontTemplate: build.mutation<IActionResponse, ISaveStorefrontTemplateRequest>({
            query: (arg) => ({
                url: 'storefront/storefronts/templates',
                method: 'POST',
                body: arg
            }),
            invalidatesTags: () => [{type: STOREFRONT_TEMPLATES_TYPE, id: LIST}]
        }),
        updateStorefrontTemplate: build.mutation<IActionResponse, {id: string, template: IStorefrontTemplate}>({
            query: (arg) => ({
                url: `storefront/storefronts/templates/${arg.id}`,
                method: 'PUT',
                body: arg.template
            }),
            invalidatesTags: (_result, _error, arg) => [
                {type: STOREFRONT_TEMPLATES_TYPE, id: arg.id},
                {type: STOREFRONT_TEMPLATES_TYPE, id: LIST}
            ]
        }),
        deleteStorefrontTemplate: build.mutation<IActionResponse, string>({
            query: (id) => ({
                url: `storefront/storefronts/templates/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_result, _error, id) => [
                {type: STOREFRONT_TEMPLATES_TYPE, id},
                {type: STOREFRONT_TEMPLATES_TYPE, id: LIST}
            ]
        }),
        uploadStorefrontTemplateGalleryMedia: build.mutation<IUploadFileResponse, {id: string, file: ArrayBuffer, fileName: string}>({
            query: (arg) => ({
                url: `storefront/storefronts/templates/${arg.id}/gallery-media`,
                method: 'POST',
                body: {
                    file: ArrayBufferToBase64(arg.file),
                    fileName: arg.fileName,
                }
            }),
            invalidatesTags: (_result, _error, arg) => [
                {type: STOREFRONT_TEMPLATES_TYPE, id: arg.id},
                {type: STOREFRONT_TEMPLATES_TYPE, id: LIST}
            ]
        }),
        deleteStorefrontTemplateGalleryMedia: build.mutation<IActionResponse, {id: string, itemId: string}>({
            query: (arg) => ({
                url: `storefront/storefronts/templates/${arg.id}/gallery-media/${arg.itemId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_result, _error, arg) => [
                {type: STOREFRONT_TEMPLATES_TYPE, id: arg.id},
                {type: STOREFRONT_TEMPLATES_TYPE, id: LIST}
            ]
        }),
        // What applying a template to an EXISTING storefront would change. Read-only, and deliberately not
        // cached against the template alone: the answer depends on the storefront's current state, which the
        // customer may have just changed in the editor.
        getStorefrontTemplateApplyPlan: build.query<IStorefrontTemplateApplyPlan, {storefrontId: string, templateId: string}>({
            query: (arg) => `storefront/storefronts/${arg.storefrontId}/apply-template/${arg.templateId}/plan`,
            providesTags: (_result, _error, arg) => [
                {type: STOREFRONT_TEMPLATES_TYPE, id: `plan-${arg.storefrontId}-${arg.templateId}`}
            ]
        }),
        // Applies the edited plan to the storefront's DRAFT. Invalidates everything the apply can touch -
        // settings, sections, styles and banners - plus the plan itself, which is now stale by definition.
        applyStorefrontTemplate: build.mutation<IActionResponse, {storefrontId: string, request: IApplyStorefrontTemplateRequest}>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefrontId}/apply-template`,
                method: 'POST',
                body: arg.request
            }),
            invalidatesTags: (_result, _error, arg) => [
                {type: STOREFRONT_SETTINGS, id: arg.storefrontId},
                {type: STOREFRONT_SETTINGS, id: LIST},
                {type: STOREFRONT_SECTIONS, id: arg.storefrontId},
                {type: STOREFRONT_SECTIONS, id: LIST},
                {type: STOREFRONT_STYLES, id: arg.storefrontId},
                {type: STOREFRONT_STYLES, id: LIST},
                // Storefront-scoped, because that is the only tag the banners query provides - a LIST tag
                // alone matched nothing, so the banners were never refetched after an apply.
                {type: STOREFRONT_BANNERS, id: arg.storefrontId},
                {type: STOREFRONT_TEMPLATES_TYPE, id: `plan-${arg.storefrontId}-${arg.request.templateId}`}
            ]
        }),
        // Provision a new storefront from a template; invalidate the storefront list so it appears.
        createStorefrontFromTemplate: build.mutation<IActionResponse, ICreateStorefrontFromTemplateRequest>({
            query: (arg) => ({
                url: 'storefront/storefronts/from-template',
                method: 'POST',
                body: arg
            }),
            invalidatesTags: () => [{type: STOREFRONT_SETTINGS, id: LIST}]
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetStorefrontTemplatesQuery,
    useGetStorefrontTemplateQuery,
    useSaveStorefrontTemplateMutation,
    useUpdateStorefrontTemplateMutation,
    useDeleteStorefrontTemplateMutation,
    useUploadStorefrontTemplateGalleryMediaMutation,
    useDeleteStorefrontTemplateGalleryMediaMutation,
    useCreateStorefrontFromTemplateMutation,
    useGetStorefrontTemplateApplyPlanQuery,
    useApplyStorefrontTemplateMutation,
} = storefrontTemplatesApi
